import React, { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import { GOOGLE_MAP_API_URL, GOOGLE_MAP_URL } from '../../../../../configs/url-constants';
import { GOOGLE_MAPS_API_KEY } from '../../../../../configs/envs';

import { AddressValue, labelSx, parseServiceAddress, valueSx } from './LocationHelpers';

// ── Styles ───────────────────────────────────────────────────

const fieldInputSx = {
  fontSize: '12px',
  border: '1px solid',
  borderColor: 'v4.borderDark',
  borderRadius: 1,
  px: 1,
  py: 0.5,
  width: '100%',
};

const toggleBtnSx = (active: boolean) => ({
  fontSize: '11px',
  fontWeight: active ? 600 : 400,
  px: 1.5,
  py: 0.5,
  cursor: 'pointer',
  borderRadius: '4px',
  bgcolor: active ? 'v4.accent' : 'transparent',
  color: active ? 'common.white' : 'text.secondary',
  border: 'none',
  fontFamily: 'inherit',
  '&:hover': active ? {} : { bgcolor: 'v4.tableHover' },
});

// ── EditableAddress ──────────────────────────────────────────

/** Floating address editor with Google Places autocomplete + manual entry. */
export const EditableAddress: React.FC<{
  serviceAddress: unknown;
  displayValue: string;
  onSave?: (value: Record<string, unknown>) => void;
}> = ({ serviceAddress, displayValue, onSave }) => {
  const anchorRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'google' | 'manual'>('google');
  const [fields, setFields] = useState<AddressValue>(() =>
    parseServiceAddress(serviceAddress),
  );

  const handleOpen = () => {
    setFields(parseServiceAddress(serviceAddress));
    setMode('google');
    setOpen(true);
  };

  const handleSave = () => {
    setOpen(false);
    if (!onSave) return;
    if (mode === 'manual') {
      onSave({
        isManual: true,
        line1: fields.line1 ?? '',
        line2: fields.line2 ?? '',
        suburb: fields.suburb ?? '',
        city: fields.city ?? '',
        stateName: fields.stateName ?? '',
        postCode: fields.postCode ?? '',
        countryName: fields.countryName ?? 'Australia',
        countryId: fields.countryId ?? 1,
        addressType: 'Service',
      });
    }
  };

  const handleCancel = () => setOpen(false);

  const updateField = (key: keyof AddressValue, val: string) =>
    setFields((prev) => ({ ...prev, [key]: val }));

  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={labelSx}>Service Address</Typography>
      <Typography
        ref={anchorRef}
        sx={{
          ...valueSx,
          cursor: onSave ? 'pointer' : 'default',
          '&:hover': onSave
            ? { bgcolor: 'v4.tableHover', borderRadius: 1, px: 0.5, mx: -0.5 }
            : {},
        }}
        onClick={() => { if (onSave) handleOpen(); }}
      >
        {displayValue || '—'}
      </Typography>

      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1400 }}>
        <ClickAwayListener onClickAway={handleCancel}>
          <Paper elevation={8} sx={{ width: 400, p: 2, mt: 0.5, border: '1px solid', borderColor: 'v4.border', borderRadius: 2 }}>
            {/* Mode toggle */}
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
              <Box component="button" sx={toggleBtnSx(mode === 'google')} onClick={() => setMode('google')}>Google Lookup</Box>
              <Box component="button" sx={toggleBtnSx(mode === 'manual')} onClick={() => setMode('manual')}>Manual Entry</Box>
            </Box>

            {mode === 'google' ? (
              <GoogleLookupPlaceholder />
            ) : (
              <ManualAddressForm fields={fields} updateField={updateField} />
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.5 }}>
              <Box component="button" onClick={handleCancel}
                sx={{ fontSize: '11px', px: 1.5, py: 0.5, border: 'none', bgcolor: 'transparent', cursor: 'pointer', color: 'text.secondary', fontFamily: 'inherit', '&:hover': { color: 'text.primary' } }}>
                Cancel
              </Box>
              <Box component="button" onClick={handleSave}
                sx={{ fontSize: '11px', fontWeight: 600, px: 2, py: 0.5, border: 'none', borderRadius: '4px', bgcolor: 'v4.accent', color: 'common.white', cursor: 'pointer', fontFamily: 'inherit', '&:hover': { opacity: 0.9 } }}>
                Save
              </Box>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

// ── Internal sub-components ──────────────────────────────────

const GoogleLookupPlaceholder: React.FC = () => (
  <Box sx={{ py: 2, textAlign: 'center' }}>
    <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 28, mb: 0.5 }} />
    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
      Google Address Lookup requires API key configuration for this domain.
    </Typography>
    <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.5 }}>
      Use Manual Entry to update the address, or configure the Google Maps API key for localhost.
    </Typography>
  </Box>
);

const ManualAddressForm: React.FC<{
  fields: AddressValue;
  updateField: (key: keyof AddressValue, val: string) => void;
}> = ({ fields, updateField }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
    <InputBase placeholder="Line 1 *" value={fields.line1 ?? ''} onChange={(e) => updateField('line1', e.target.value)}
      inputProps={{ 'aria-label': 'Address line 1' }} sx={fieldInputSx} autoFocus />
    <InputBase placeholder="Line 2" value={fields.line2 ?? ''} onChange={(e) => updateField('line2', e.target.value)}
      inputProps={{ 'aria-label': 'Address line 2' }} sx={fieldInputSx} />
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
      <InputBase placeholder="Suburb *" value={fields.suburb ?? ''} onChange={(e) => updateField('suburb', e.target.value)}
        inputProps={{ 'aria-label': 'Suburb' }} sx={fieldInputSx} />
      <InputBase placeholder="City" value={fields.city ?? ''} onChange={(e) => updateField('city', e.target.value)}
        inputProps={{ 'aria-label': 'City' }} sx={fieldInputSx} />
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
      <InputBase placeholder="State *" value={fields.stateName ?? ''} onChange={(e) => updateField('stateName', e.target.value)}
        inputProps={{ 'aria-label': 'State' }} sx={fieldInputSx} />
      <InputBase placeholder="Postcode *" value={fields.postCode ?? ''} onChange={(e) => updateField('postCode', e.target.value)}
        inputProps={{ 'aria-label': 'Postcode' }} sx={fieldInputSx} />
    </Box>
  </Box>
);

// ── MapSquare ────────────────────────────────────────────────

/** Static Google Maps image -- loads once, cached by browser. */
export const MapSquare: React.FC<{ address: string }> = ({ address }) => {
  const mapQuery = encodeURIComponent(address);
  const staticSrc = GOOGLE_MAPS_API_KEY
    ? `${GOOGLE_MAP_API_URL}/api/staticmap?center=${mapQuery}&zoom=15&size=600x450&scale=2&markers=color:red%7C${mapQuery}&key=${GOOGLE_MAPS_API_KEY}`
    : '';
  const mapsLink = `${GOOGLE_MAP_URL}?q=${mapQuery}`;

  return (
    <Box
      sx={{
        borderRadius: '6px',
        overflow: 'hidden',
        aspectRatio: '16/10',
        width: '100%',
        position: 'relative',
        bgcolor: 'grey.200',
        cursor: 'pointer',
      }}
      onClick={() => window.open(mapsLink, '_blank', 'noopener')}
      role="link"
      aria-label="Open in Google Maps"
    >
      {staticSrc ? (
        <Box
          component="img"
          src={staticSrc}
          alt={`Map of ${address}`}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          loading="lazy"
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>Map unavailable</Typography>
        </Box>
      )}
      <Box
        sx={{
          position: 'absolute', bottom: 6, right: 6,
          bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '4px',
          px: 1, py: 0.25,
          '&:hover': { bgcolor: 'common.white' },
        }}
      >
        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: 'primary.main' }}>Open in Maps</Typography>
      </Box>
    </Box>
  );
};
